/**
 * Sección "Analyze overrides" para forms de Module config (Fase 41).
 *
 * Vive en `module_config.<slug>.config.analyze`. Mergea contra `analyze_config`
 * global (ver `mergeAnalyzeConfig` en backend). Todos los campos son
 * opcionales — si están ausentes, heredan del global.
 */
import { type ReactElement } from 'react';
import type {
  AnalyzeConfig,
  CriticStrictness,
} from '../../api/admin/analyze-config.js';

export interface AnalyzeOverrideValue {
  enabled?: boolean;
  minTurnCount?: number;
  minDurationSeconds?: number;
  criticStrictness?: CriticStrictness;
  mutedCategories?: string[];
  excludeFromMatcher?: boolean;
}

export interface AnalyzeOverrideSectionProps {
  value: AnalyzeOverrideValue | undefined;
  onChange: (next: AnalyzeOverrideValue | undefined) => void;
  disabled?: boolean | undefined;
  /** Snapshot global para mostrar "heredado: X" en cada campo. Opcional. */
  globalSnapshot?: AnalyzeConfig | null | undefined;
}

export function AnalyzeOverrideSection({
  value,
  onChange,
  disabled,
  globalSnapshot,
}: AnalyzeOverrideSectionProps): ReactElement {
  const v = value ?? {};

  function patch(key: keyof AnalyzeOverrideValue, raw: unknown): void {
    const next: AnalyzeOverrideValue = { ...v };
    const empty = raw === undefined || raw === null || raw === '';
    if (empty) {
      delete next[key];
    } else {
      (next as Record<string, unknown>)[key] = raw;
    }
    onChange(Object.keys(next).length === 0 ? undefined : next);
  }

  const heredado = (txt: string | number | boolean | undefined): string => {
    if (txt === undefined || txt === null) return '(global)';
    return `hereda: ${String(txt)}`;
  };

  return (
    <section className="config-section">
      <header className="config-section-head">
        <h4 className="config-section-title">Analyze overrides (Fase 41)</h4>
        <p className="config-section-sub">
          Mergea contra <code>analyze_config</code> global. Sólo se aplican si{' '}
          <code>global.enabled = true</code>. Dejar vacío = heredar.
        </p>
      </header>
      <div className="config-fields">
        <div className="config-field">
          <label className="config-field-label" htmlFor="ov-enabled">
            Enabled (override)
          </label>
          <select
            id="ov-enabled"
            className="config-input"
            value={v.enabled === undefined ? 'inherit' : v.enabled ? 'true' : 'false'}
            onChange={(e) => {
              const x = e.target.value;
              if (x === 'inherit') return patch('enabled', undefined);
              patch('enabled', x === 'true');
            }}
            disabled={disabled}
          >
            <option value="inherit">Heredar del global</option>
            <option value="true">Forzar ON</option>
            <option value="false">Forzar OFF (apaga el módulo)</option>
          </select>
          <p className="config-field-hint">
            Forzar OFF gana sobre el global. Forzar ON requiere que el global
            también esté ON (AND).
          </p>
        </div>

        <div className="config-field">
          <label className="config-field-label" htmlFor="ov-min-turn">
            Min turn count
          </label>
          <input
            id="ov-min-turn"
            type="number"
            className="config-input"
            min={0}
            max={1000}
            placeholder={heredado(globalSnapshot?.minTurnCount)}
            value={v.minTurnCount ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) return patch('minTurnCount', undefined);
              const n = Number(raw);
              if (!Number.isFinite(n) || n < 0) return;
              patch('minTurnCount', Math.floor(n));
            }}
            disabled={disabled}
          />
        </div>

        <div className="config-field">
          <label className="config-field-label" htmlFor="ov-min-dur">
            Min duration (segundos)
          </label>
          <input
            id="ov-min-dur"
            type="number"
            className="config-input"
            min={0}
            max={86400}
            placeholder={heredado(globalSnapshot?.minDurationSeconds)}
            value={v.minDurationSeconds ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) return patch('minDurationSeconds', undefined);
              const n = Number(raw);
              if (!Number.isFinite(n) || n < 0) return;
              patch('minDurationSeconds', Math.floor(n));
            }}
            disabled={disabled}
          />
        </div>

        <div className="config-field">
          <label className="config-field-label" htmlFor="ov-strict">
            Critic strictness
          </label>
          <select
            id="ov-strict"
            className="config-input"
            value={v.criticStrictness ?? 'inherit'}
            onChange={(e) => {
              const x = e.target.value;
              if (x === 'inherit') return patch('criticStrictness', undefined);
              patch('criticStrictness', x);
            }}
            disabled={disabled}
          >
            <option value="inherit">
              Heredar del global ({globalSnapshot?.criticStrictness ?? '—'})
            </option>
            <option value="strict">strict</option>
            <option value="balanced">balanced</option>
            <option value="permissive">permissive</option>
          </select>
        </div>

        <div className="config-field">
          <label className="config-field-label" htmlFor="ov-exclude-matcher">
            Excluir del matcher de regression
          </label>
          <div className="config-field-row">
            <input
              id="ov-exclude-matcher"
              type="checkbox"
              checked={v.excludeFromMatcher ?? false}
              onChange={(e) => {
                if (e.target.checked) patch('excludeFromMatcher', true);
                else patch('excludeFromMatcher', undefined);
              }}
              disabled={disabled}
            />
            <span className="t-meta">
              Si ON, salta el matcher aunque global.matcherEnabled = true.
            </span>
          </div>
        </div>

        <div className="config-field">
          <label className="config-field-label">Muted categories (extra)</label>
          <p className="config-field-hint">
            Se unionan con global.mutedCategories. Una por línea o separadas por
            coma.
          </p>
          <textarea
            className="config-input"
            rows={3}
            placeholder="pricing_outlier&#10;latency"
            value={(v.mutedCategories ?? []).join('\n')}
            onChange={(e) => {
              const next = e.target.value
                .split(/[\n,]/g)
                .map((x) => x.trim())
                .filter(Boolean);
              patch('mutedCategories', next.length === 0 ? undefined : next);
            }}
            disabled={disabled}
          />
        </div>
      </div>
    </section>
  );
}
